import { forwardRef } from "react";
import { motion, type HTMLMotionProps } from "framer-motion";
import { cn } from "@/lib/cn";
import { haptic, type HapticIntensity } from "@/lib/haptics";
import { HAIRLINE_RING_CLASS } from "@/components/premium";

/**
 * The variant skins, exported so a surface that must be an ANCHOR (a real
 * `<a href>` — right-clickable, middle-clickable, and announced as a link)
 * can wear the identical skin instead of hand-rolling a second one.
 * `Button` itself renders a `motion.button`, so it cannot serve that case.
 */
// Module 06 task 6 (AC-31) — every variant's `hover:` now carries an
// `active:` twin (same property, one notch further) so a press has visible
// feedback on top of the hover state, not just the shared `whileTap` scale
// motion below. Purely additive: no existing hover/base rule changed.
export const BUTTON_VARIANTS = {
  // The one primary is `interactive` blue (DESIGN-SYSTEM.md §3.2, amended
  // 2026-08-21). Emerald (`accent`/`strenn-emerald`) is reserved for success /
  // hardware-confirmed / AAL-milestone STATES only (CLAUDE.md §Design
  // Constraints), never a call-to-action. History: AC-19 moved this off the
  // static `bg-strenn-emerald text-white` (<4.5:1 in both themes) onto the
  // `accent`/`onAccent` pair; that pair is now retired as a CTA skin, and the
  // theme-flipped `onInteractive` ink clears 4.5:1 in both themes
  // (tests/unit/design-token-contrast.test.ts).
  primary:   "bg-interactive text-on-interactive shadow-inner hover:brightness-110 active:brightness-125 focus:ring-2 focus:ring-interactive/50",
  /** Neutral high-contrast primary action for text-led security guidance. */
  primaryAccessible: "bg-text-primary text-bg-primary shadow-inner hover:bg-text-secondary active:bg-text-secondary focus:ring-2 focus:ring-text-primary/50",
  secondary: `border border-border-default bg-bg-elevated text-text-primary ${HAIRLINE_RING_CLASS} hover:border-interactive/40 hover:bg-bg-overlay active:bg-bg-overlay transition-colors`,
  ghost:     "text-text-secondary hover:bg-bg-overlay hover:text-text-primary active:bg-bg-overlay active:text-text-primary focus-visible:ring-2 focus-visible:ring-interactive/50 focus-visible:outline-none",
  critical:  "bg-critical text-white hover:brightness-110 active:brightness-125 focus:ring-2 focus:ring-critical/50",
} as const;

/**
 * `compact` is the row-sized button: the same 44px touch target, one step down
 * in padding and type, for actions that sit inside a list row (the request
 * rows — `@/components/ui/request-row`). It replaces the hand-written
 * `px-3 text-[length:var(--text-caption-size)]` copies (#3567).
 */
export const BUTTON_SIZES = {
  default: "",
  compact: "px-3 text-[length:var(--text-caption-size)]",
} as const;

const hapticMap: Record<string, HapticIntensity> = {
  primary: "medium",
  primaryAccessible: "medium",
  secondary: "light",
  ghost: "light",
  critical: "error",
};

interface ButtonProps extends Omit<HTMLMotionProps<"button">, "ref"> {
  variant?: keyof typeof BUTTON_VARIANTS;
  size?: keyof typeof BUTTON_SIZES;
  loading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = "primary", size = "default", loading, disabled, children, onClick, ...props }, ref) => {
    return (
      <motion.button
        ref={ref}
        whileTap={disabled ? undefined : { scale: 0.97 }}
        aria-busy={loading ? "true" : undefined}
        className={cn(
          "min-h-[44px] min-w-0 max-w-full px-5 py-2 rounded-[var(--radius-md)] font-medium text-[15px]",
          "transition-colors duration-[var(--duration-fast)]",
          "disabled:opacity-40 disabled:pointer-events-none",
          "inline-flex items-center justify-center gap-2",
          BUTTON_VARIANTS[variant],
          BUTTON_SIZES[size],
          className,
        )}
        disabled={disabled || loading}
        onClick={(e) => {
          haptic(hapticMap[variant]);
          onClick?.(e);
        }}
        {...props}
      >
        {loading ? (
          <>
            <span className="sr-only">Loading…</span>
            <span className="flex gap-1" aria-hidden="true">
              <span className="w-1.5 h-1.5 rounded-full bg-current animate-bounce [animation-delay:0ms]" />
              <span className="w-1.5 h-1.5 rounded-full bg-current animate-bounce [animation-delay:150ms]" />
              <span className="w-1.5 h-1.5 rounded-full bg-current animate-bounce [animation-delay:300ms]" />
            </span>
          </>
        ) : children}
      </motion.button>
    );
  }
);
Button.displayName = "Button";
